// client/src/components/DataQueryPanel.js
// Panel for natural language data queries with conversation history and results display
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FaPaperPlane, FaRobot, FaUser, FaDatabase, FaCode, FaDownload, FaTrash, FaLightbulb, FaSpinner, FaTimes } from 'react-icons/fa';
import { MdQuestionAnswer } from 'react-icons/md';
import DynamicResultsTable from './DynamicResultsTable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Example queries for hints
const EXAMPLE_QUERIES = [
  "How many counties do we have?",
  "Show me enriched owners in Harris county with more than 50 acres",
  "What's the total count of non-enriched records?",
  "Get email verification stats for county 5",
  "List the top 10 counties by enriched owner count",
  "How many owners have verified emails?",
  "Show owners with email_1 that have zerobounce status 'valid'",
  "What's the average acreage for enriched owners?",
];

const DataQueryPanel = () => {
  // Conversation state
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      type: 'text',
      content: "Hi! I'm your data assistant. Ask me anything about your leads database. I can help you with:\n\n• **Counts & Stats**: \"How many enriched owners in Harris county?\"\n• **Data Lookup**: \"Show owners with more than 100 acres\"\n• **Email Verification**: \"Get email verification stats for county 5\"\n• **Analysis**: \"What's the average acreage by county?\"\n\nType your question below or click an example to get started."
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(null);
  
  // Results state
  const [currentResults, setCurrentResults] = useState(null);
  const [showSqlModal, setShowSqlModal] = useState(false);
  const [currentSql, setCurrentSql] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check service status on mount
  useEffect(() => {
    checkServiceStatus();
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const checkServiceStatus = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/data-query/status`, {
        credentials: 'include'
      });
      const data = await res.json();
      setServiceStatus(data);
    } catch (err) {
      setServiceStatus({ available: false });
    }
  };

  // Build conversation history for context
  const buildConversationHistory = () => {
    return messages
      .filter(m => m.role === 'user' || (m.role === 'assistant' && m.type === 'text'))
      .slice(-10) // Keep last 10 messages for context
      .map(m => ({
        role: m.role,
        content: m.content
      }));
  };

  // Send query to backend
  const sendQuery = async (queryText = input) => {
    if (!queryText.trim()) return;
    
    const userMessage = { role: 'user', type: 'text', content: queryText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setCurrentResults(null);

    try {
      const res = await fetch(`${API_BASE_URL}/data-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          prompt: queryText,
          conversationHistory: buildConversationHistory(),
          pageSize: 100
        })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        const errorMessage = {
          role: 'assistant',
          type: 'error',
          content: data.message || 'Sorry, something went wrong. Please try again.'
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }

      // Handle different response types
      switch (data.type) {
        case 'clarification':
          const clarifyMessage = {
            role: 'assistant',
            type: 'clarification',
            content: data.message,
            questions: data.questions
          };
          setMessages(prev => [...prev, clarifyMessage]);
          break;

        case 'answer':
          const answerMessage = {
            role: 'assistant',
            type: 'text',
            content: data.message,
            explanation: data.explanation
          };
          setMessages(prev => [...prev, answerMessage]);
          break;

        case 'data':
          const dataMessage = {
            role: 'assistant',
            type: 'data',
            content: data.explanation || 'Here are your results:',
            data: data.data,
            fields: data.fields,
            rowCount: data.rowCount,
            sql: data.sql,
            rpcName: data.rpcName,
            rpcParams: data.rpcParams
          };
          setMessages(prev => [...prev, dataMessage]);
          setCurrentResults({
            data: data.data,
            fields: data.fields,
            rowCount: data.rowCount,
            sql: data.sql
          });
          break;

        default:
          const defaultMessage = {
            role: 'assistant',
            type: 'text',
            content: 'I received your request but couldn\'t process it properly. Please try rephrasing.'
          };
          setMessages(prev => [...prev, defaultMessage]);
      }
    } catch (err) {
      console.error('Query error:', err);
      const errorMessage = {
        role: 'assistant',
        type: 'error',
        content: 'Network error. Please check your connection and try again.'
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to send query');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuery();
  };

  // Handle example click
  const handleExampleClick = (example) => {
    setInput(example);
    inputRef.current?.focus();
  };

  // Export results as CSV
  const handleExport = async () => {
    if (!currentResults?.data) {
      toast.error('No data to export');
      return;
    }

    try {
      // Get the last user message for the query
      const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
      if (!lastUserMessage) return;

      const res = await fetch(`${API_BASE_URL}/data-query/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          prompt: lastUserMessage.content,
          conversationHistory: buildConversationHistory()
        })
      });

      if (!res.ok) {
        throw new Error('Export failed');
      }

      // Download the CSV
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `query_export_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  // Clear conversation
  const handleClear = () => {
    setMessages([messages[0]]); // Keep the initial greeting
    setCurrentResults(null);
    setInput('');
  };

  // View SQL modal
  const handleViewSql = (sql) => {
    setCurrentSql(sql);
    setShowSqlModal(true);
  };

  // Render a message
  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    
    return (
      <div
        key={index}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`flex max-w-[85%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar */}
          <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isUser ? 'bg-blue-600' : 'bg-gray-600'
            }`}>
              {isUser ? <FaUser className="text-white" size={14} /> : <FaRobot className="text-white" size={14} />}
            </div>
          </div>
          
          {/* Message content */}
          <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
            <div className={`rounded-lg px-4 py-3 ${
              isUser 
                ? 'bg-blue-600 text-white' 
                : message.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {/* Text content with markdown-like formatting */}
              <div className="prose prose-sm max-w-none">
                {message.content.split('\n').map((line, i) => (
                  <p key={i} className={`${i > 0 ? 'mt-2' : ''} ${line.startsWith('•') ? 'ml-2' : ''}`}>
                    {line.split(/(\*\*.*?\*\*)/).map((part, j) => 
                      part.startsWith('**') && part.endsWith('**') 
                        ? <strong key={j}>{part.slice(2, -2)}</strong>
                        : part
                    )}
                  </p>
                ))}
              </div>
              
              {/* Clarification questions */}
              {message.type === 'clarification' && message.questions && (
                <div className="mt-3 space-y-2">
                  {message.questions.map((q, i) => (
                    <div key={i} className="flex items-start">
                      <MdQuestionAnswer className="mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm">{q}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Data results indicator */}
            {message.type === 'data' && message.data && (
              <div className="mt-2 flex items-center space-x-2 text-xs text-gray-500">
                <FaDatabase className="text-green-600" />
                <span>{message.rowCount} rows returned</span>
                {message.sql && (
                  <button
                    onClick={() => handleViewSql(message.sql)}
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <FaCode className="mr-1" />
                    View SQL
                  </button>
                )}
                {message.rpcName && (
                  <span className="text-purple-600">
                    RPC: {message.rpcName}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border h-full flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
        <div className="flex items-center">
          <FaRobot className="mr-3" size={24} />
          <div>
            <h2 className="text-lg font-semibold">Data Query Assistant</h2>
            <p className="text-blue-100 text-sm">Ask questions about your leads database</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {serviceStatus?.available && (
            <span className="flex items-center text-xs text-green-200">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
              Online
            </span>
          )}
          <button
            onClick={handleClear}
            className="p-2 hover:bg-white/10 rounded-md transition-colors"
            title="Clear conversation"
          >
            <FaTrash size={14} />
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Conversation panel */}
        <div className="flex-1 flex flex-col border-r border-gray-200">
          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => renderMessage(msg, i))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start mb-4">
                <div className="flex items-center bg-gray-100 rounded-lg px-4 py-3">
                  <FaSpinner className="animate-spin text-blue-600 mr-2" />
                  <span className="text-gray-600">Analyzing your query...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Example queries */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2">
              <div className="flex items-center text-xs text-gray-500 mb-2">
                <FaLightbulb className="mr-1 text-yellow-500" />
                Try an example:
              </div>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_QUERIES.slice(0, 4).map((example, i) => (
                  <button
                    key={i}
                    onClick={() => handleExampleClick(example)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-gray-200 p-4">
            <form onSubmit={handleSubmit} className="flex space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question about your data..."
                disabled={loading || !serviceStatus?.available}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={loading || !input.trim() || !serviceStatus?.available}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <FaPaperPlane className="mr-2" />
                Send
              </button>
            </form>
            {!serviceStatus?.available && (
              <p className="text-xs text-red-500 mt-2">
                Service unavailable. Please check OpenAI and database configuration.
              </p>
            )}
          </div>
        </div>

        {/* Results panel */}
        <div className="w-full lg:w-1/2 flex flex-col bg-gray-50 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
            <h3 className="font-medium text-gray-900 flex items-center">
              <FaDatabase className="mr-2 text-gray-500" />
              Query Results
            </h3>
            {currentResults?.data && (
              <button
                onClick={handleExport}
                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <FaDownload className="mr-1.5" size={12} />
                Export CSV
              </button>
            )}
          </div>
          <div className="flex-1 overflow-auto p-4">
            {currentResults?.data ? (
              <DynamicResultsTable
                data={currentResults.data}
                fields={currentResults.fields}
                onExport={handleExport}
                pageSize={50}
                maxHeight="calc(100vh - 400px)"
                title={`${currentResults.rowCount} Results`}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <FaDatabase className="mx-auto text-gray-300 mb-3" size={48} />
                  <p>Results will appear here</p>
                  <p className="text-sm text-gray-400 mt-1">Ask a question to query your data</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SQL Modal */}
      {showSqlModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSqlModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-900 flex items-center">
                <FaCode className="mr-2 text-purple-600" />
                Generated SQL Query
              </h3>
              <button
                onClick={() => setShowSqlModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                {currentSql}
              </pre>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(currentSql);
                  toast.success('SQL copied to clipboard');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <FaCode className="mr-2" />
                Copy SQL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQueryPanel;
